/**
 * AFIP/ARCA (Argentina) invoice PDF builder.
 *
 * Pure, framework-agnostic module: no Vue imports, no side effects.
 * Ported from cartadigital-pos (app/domain/invoicing/ar-afip/invoice-pdf.ts);
 * keep both copies logically identical.
 *
 * Layout follows RG 1415 (emitter/receptor/detail), RG 4892 (QR) and
 * Ley 27.743 (fiscal transparency block for final-consumer invoices).
 */

import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { CJK_PDF_FONT, containsCjk } from './cjk-text';
import { buildAfipQrUrl } from './ar-afip-qr-payload';

export interface IArAfipInvoicePdfLine {
  description: string;
  amount: number;
  unitPrice: number;
  total: number;
}

export interface IArAfipInvoicePdfContext {
  /** Cached/fetched invoice payload (invoicingItem.data + total). */
  invoice: { data: Record<string, unknown>; total: number };
  emitter: {
    razonSocial: string | null;
    condicionIva: string | null;
    domicilio: string | null;
    ingresosBrutos: string | null;
    /** ISO yyyy-mm-dd */
    inicioActividades: string | null;
  };
  lines: IArAfipInvoicePdfLine[];
  adjustments: { delivery: number; discount: number; roundDifference: number };
  currencySymbol: string;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function toNumber(value: unknown, fallback = 0): number {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number(value)
      : NaN;
  return Number.isFinite(n) ? n : fallback;
}

function toTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** yyyy-mm-dd -> dd/mm/yyyy (returns the input when it does not match). */
function formatInvoiceDate(value: unknown): string {
  const raw = toTrimmedString(value);
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return ymd ? `${ymd[3]}/${ymd[2]}/${ymd[1]}` : raw;
}

function padVoucherNumber(value: unknown, length: number): string {
  return String(Math.trunc(toNumber(value, 0))).padStart(length, '0');
}

interface IComprobanteKind {
  letter: string;
  cod: string;
}

function resolveComprobanteKind(tipoCmp: number): IComprobanteKind {
  if (tipoCmp === 1) return { letter: 'A', cod: '01' };
  if (tipoCmp === 6) return { letter: 'B', cod: '06' };
  if (tipoCmp === 11) return { letter: 'C', cod: '11' };
  return { letter: '?', cod: String(tipoCmp).padStart(2, '0') };
}

function resolveReceptorDocLabel(tipoDocRec: number): string {
  if (tipoDocRec === 80) return 'CUIT';
  if (tipoDocRec === 86) return 'CUIL';
  if (tipoDocRec === 96) return 'DNI';
  return 'Consumidor Final';
}

/** RG 5616 receptor IVA condition labels (data.condIvaRec). */
const RECEPTOR_CONDICION_LABELS: Record<number, string> = {
  1: 'Responsable Inscripto',
  4: 'IVA Sujeto Exento',
  5: 'Consumidor Final',
  6: 'Responsable Monotributo',
  7: 'Sujeto No Categorizado',
};

/**
 * Declared receptor condition: prefer the persisted condIvaRec (what was
 * actually sent to ARCA); legacy invoices fall back to the tipoCmp heuristic.
 */
function resolveReceptorCondicion(
  condIvaRec: unknown,
  tipoCmp: number
): string {
  const label = RECEPTOR_CONDICION_LABELS[toNumber(condIvaRec, NaN)];
  if (label) return label;
  return tipoCmp === 1 ? 'Responsable Inscripto' : 'Consumidor Final';
}

/**
 * Font override for user-provided text (item names, emitter profile):
 * Roboto renders CJK as tofu squares, so such text switches to the
 * CJK-capable font that the pdf-engine registers on demand.
 */
function fontFor(text: string): { font: string } | Record<string, never> {
  return containsCjk(text) ? { font: CJK_PDF_FONT } : {};
}

export function buildInvoicePdfFilename(data: Record<string, unknown>): string {
  const ptoVta = padVoucherNumber(data.ptoVta, 4);
  const nroCmp = padVoucherNumber(data.nroCmp, 8);
  return `Factura-${ptoVta}-${nroCmp}.pdf`;
}

/**
 * Deterministic consistency guard: the itemized detail is only rendered when
 * it reconciles (to the cent) with the CAE-authorized total. Group invoices,
 * stale local caches or missing item relations fall back to a single
 * "Consumo" row so the PDF never contradicts the authorized amount.
 */
function resolveRenderableLines(
  ctx: IArAfipInvoicePdfContext
): {
  lines: IArAfipInvoicePdfLine[];
  adjustments: IArAfipInvoicePdfContext['adjustments'];
} {
  const fallback = {
    lines: [
      {
        description: 'Consumo',
        amount: 1,
        unitPrice: round2(ctx.invoice.total),
        total: round2(ctx.invoice.total),
      },
    ],
    adjustments: { delivery: 0, discount: 0, roundDifference: 0 },
  };

  if (ctx.lines.length === 0) return fallback;
  if (ctx.lines.some((line) => toTrimmedString(line.description) === '')) {
    return fallback;
  }

  const { delivery, discount, roundDifference } = ctx.adjustments;
  const sum = round2(
    ctx.lines.reduce((acc, line) => acc + line.total, 0) +
      delivery -
      discount +
      roundDifference
  );

  if (Math.abs(round2(sum - ctx.invoice.total)) > 0.01) return fallback;

  return { lines: ctx.lines, adjustments: ctx.adjustments };
}

export function buildArAfipInvoicePdf(
  ctx: IArAfipInvoicePdfContext
): TDocumentDefinitions {
  const data = ctx.invoice.data ?? {};
  const money = (value: number): string =>
    `${ctx.currencySymbol}${round2(value).toFixed(2)}`;

  const tipoCmp = toNumber(data.tipoCmp, 0);
  const tipoDocRec = toNumber(data.tipoDocRec, 99);
  const nroDocRec = toNumber(data.nroDocRec, 0);
  const impIVA = toNumber(data.impIVA, 0);
  const kind = resolveComprobanteKind(tipoCmp);

  // --- Header ------------------------------------------------------------
  const emitterStack: Content[] = [];
  const razonSocial = toTrimmedString(ctx.emitter.razonSocial);
  const domicilio = toTrimmedString(ctx.emitter.domicilio);
  const condicionIva = toTrimmedString(ctx.emitter.condicionIva);
  if (razonSocial) {
    emitterStack.push({
      text: razonSocial,
      bold: true,
      fontSize: 12,
      ...fontFor(razonSocial),
    });
  }
  if (domicilio) {
    emitterStack.push({ text: domicilio, ...fontFor(domicilio) });
  }
  if (condicionIva) emitterStack.push({ text: condicionIva });

  const metaStack: Content[] = [
    { text: 'FACTURA', bold: true, fontSize: 14 },
    { text: `Punto de Venta: ${padVoucherNumber(data.ptoVta, 4)}` },
    { text: `Comp. Nro: ${padVoucherNumber(data.nroCmp, 8)}` },
    { text: `Fecha de Emisión: ${formatInvoiceDate(data.fecha)}` },
    // Always the per-invoice payload CUIT (never the emitter profile's).
    { text: `CUIT: ${toTrimmedString(String(data.cuit ?? ''))}` },
  ];
  const ingresosBrutos = toTrimmedString(ctx.emitter.ingresosBrutos);
  if (ingresosBrutos) {
    metaStack.push({ text: `Ingresos Brutos: ${ingresosBrutos}` });
  }
  const inicioActividades = toTrimmedString(ctx.emitter.inicioActividades);
  if (inicioActividades) {
    metaStack.push({
      text: `Inicio de Actividades: ${formatInvoiceDate(inicioActividades)}`,
    });
  }

  const header: Content = {
    columns: [
      { width: '*', stack: emitterStack },
      {
        width: 'auto',
        table: {
          body: [
            [
              {
                stack: [
                  {
                    text: kind.letter,
                    bold: true,
                    fontSize: 22,
                    alignment: 'center',
                  },
                  {
                    text: `COD. ${kind.cod}`,
                    fontSize: 8,
                    alignment: 'center',
                  },
                ],
                margin: [8, 4, 8, 4],
              },
            ],
          ],
        },
        margin: [8, 0, 8, 0],
      },
      { width: '*', stack: metaStack, alignment: 'right' },
    ],
    columnGap: 8,
    margin: [0, 0, 0, 12],
  };

  // --- Receptor strip ----------------------------------------------------
  const receptorCondicion = resolveReceptorCondicion(data.condIvaRec, tipoCmp);
  const receptorDocLine =
    tipoDocRec === 99
      ? 'Consumidor Final'
      : `${resolveReceptorDocLabel(tipoDocRec)}: ${Math.trunc(nroDocRec)}`;
  const razonSocialRec = toTrimmedString(data.razonSocialRec);

  const receptor: Content = {
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              { text: `Condición frente al IVA: ${receptorCondicion}` },
              { text: receptorDocLine },
              ...(razonSocialRec
                ? [
                    {
                      text: `Razón social: ${razonSocialRec}`,
                      ...fontFor(razonSocialRec),
                    },
                  ]
                : []),
            ],
            margin: [8, 6, 8, 6],
          },
        ],
      ],
    },
    margin: [0, 0, 0, 12],
  };

  // --- Items table -------------------------------------------------------
  const renderable = resolveRenderableLines(ctx);

  const itemRows: Content[][] = renderable.lines.map((line) => [
    { text: line.description, ...fontFor(line.description) },
    { text: String(line.amount), alignment: 'right' },
    { text: money(line.unitPrice), alignment: 'right' },
    { text: money(line.total), alignment: 'right' },
  ]);

  const { delivery, discount, roundDifference } = renderable.adjustments;
  if (delivery !== 0) {
    itemRows.push([
      { text: 'Envío' },
      { text: '', alignment: 'right' },
      { text: '', alignment: 'right' },
      { text: money(delivery), alignment: 'right' },
    ]);
  }
  if (discount !== 0) {
    itemRows.push([
      { text: 'Descuento' },
      { text: '', alignment: 'right' },
      { text: '', alignment: 'right' },
      { text: money(-discount), alignment: 'right' },
    ]);
  }
  if (roundDifference !== 0) {
    itemRows.push([
      { text: 'Redondeo' },
      { text: '', alignment: 'right' },
      { text: '', alignment: 'right' },
      { text: money(roundDifference), alignment: 'right' },
    ]);
  }

  const itemsTable: Content = {
    table: {
      headerRows: 1,
      widths: ['*', 'auto', 'auto', 'auto'],
      body: [
        [
          { text: 'Descripción', bold: true },
          { text: 'Cantidad', bold: true, alignment: 'right' },
          { text: 'P. Unitario', bold: true, alignment: 'right' },
          { text: 'Subtotal', bold: true, alignment: 'right' },
        ],
        ...itemRows,
      ],
    },
    layout: 'lightHorizontalLines',
    margin: [0, 0, 0, 12],
  };

  // --- Totals block ------------------------------------------------------
  const totalRows: Content[][] = [];
  if (tipoCmp === 1) {
    // Legacy fallback (pre-caeVto invoices): importe - impIVA equals
    // neto + no-gravado; accepted approximation, do not "improve" it.
    const impNeto =
      data.impNeto !== undefined && data.impNeto !== null
        ? toNumber(data.impNeto, 0)
        : round2(toNumber(data.importe, ctx.invoice.total) - impIVA);
    const impTotConc = toNumber(data.impTotConc, 0);

    totalRows.push([
      { text: 'Importe Neto Gravado:' },
      { text: money(impNeto), alignment: 'right' },
    ]);
    if (impTotConc > 0) {
      totalRows.push([
        { text: 'Importe No Gravado:' },
        { text: money(impTotConc), alignment: 'right' },
      ]);
    }
    totalRows.push([
      { text: 'IVA:' },
      { text: money(impIVA), alignment: 'right' },
    ]);
  }
  totalRows.push([
    { text: 'Importe Total:', bold: true },
    { text: money(ctx.invoice.total), bold: true, alignment: 'right' },
  ]);

  const totalsBlock: Content = {
    columns: [
      { width: '*', text: '' },
      {
        width: 'auto',
        table: { body: totalRows },
        layout: 'noBorders',
      },
    ],
    margin: [0, 0, 0, 12],
  };

  const content: Content[] = [header, receptor, itemsTable, totalsBlock];

  // --- Fiscal transparency box (Ley 27.743) -------------------------------
  if ((tipoCmp === 6 || tipoCmp === 11) && impIVA > 0) {
    content.push({
      columns: [
        { width: '*', text: '' },
        {
          width: 'auto',
          table: {
            body: [
              [
                {
                  stack: [
                    {
                      text:
                        'Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)',
                      bold: true,
                      fontSize: 8,
                    },
                    { text: `IVA Contenido: ${money(impIVA)}`, fontSize: 8 },
                  ],
                  margin: [6, 4, 6, 4],
                },
              ],
            ],
          },
        },
      ],
      margin: [0, 0, 0, 12],
    });
  }

  // --- Footer: QR + CAE ----------------------------------------------------
  const caeStack: Content[] = [
    {
      text: `CAE N°: ${toTrimmedString(String(data.codAut ?? ''))}`,
      bold: true,
    },
  ];
  const caeVto = formatInvoiceDate(data.caeVto);
  if (caeVto) {
    caeStack.push({ text: `Fecha de Vto. de CAE: ${caeVto}` });
  }

  content.push({
    columns: [
      // fit >= 150pt: the RG 4892 URL yields a dense QR that becomes
      // unscannable when rendered smaller.
      { width: 'auto', qr: buildAfipQrUrl(data), fit: 150 },
      { width: '*', stack: caeStack, alignment: 'right', margin: [0, 8, 0, 0] },
    ],
    columnGap: 12,
    margin: [0, 8, 0, 0],
  });

  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { fontSize: 10 },
    info: { title: buildInvoicePdfFilename(data).replace(/\.pdf$/, '') },
    content,
  };
}
